import $ from 'jquery';
import moment from 'moment';
import { showConfirmation } from 'next/javascripts/components/dialog';
import 'next/stylesheets/components/editor-draft-tip.scss';

const waitingTime = 2000;
const loopTime = 10000;

function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

function isEqual(target, source) {
  if (Object.keys(target).length !== Object.keys(source).length) return false;
  return Object.keys(target).every(key => target[key] === source[key]);
}

export default {
  properties: {
    draftUrl: {
      type: String,
    },

    draftUpdatedAt: {
      type: String,
    },

    draftDestroyUrl: {
      type: String,
    },

    draftClearable: {
      type: Boolean,
      default: false,
    },
  },

  ready() {
    if (!this.draftUrl) return;
    this.draftData = {};

    [...this.querySelectorAll('tr-editor')].forEach((element) => {
      this.listenTo(
        element,
        'tr-editor:change',
        debounce(() => this.handleDraftChange(), waitingTime),
      );
      this.listenTo(element, 'tr-editor:editorReady', () => {
        this.draftData = this.getDraftData();
      });
    });

    [...this.querySelectorAll('tr-markdown-editor')].forEach((element) => {
      this.listenTo(element, 'tr-markdown-editor:editorReady', this.handleMDEditorReady.bind(this));
    });

    [...this.querySelectorAll('[data-draft-field="true"]')].forEach((element) => {
      if (element.nodeName.toLowerCase() === 'tr-text-field') {
        this.listenTo(element.nativeField, 'change', this.handleDraftChange.bind(this));
      } else {
        this.listenTo(element, 'change', this.handleDraftChange.bind(this));
      }
    });

    this.listenTo(this.querySelector('form'), 'submit', () => {
      this.draftDisable = true;
    });
    this.listenTo(this, 'tao:cancel', this.handleCancel.bind(this));

    this.draftTimer = setInterval(() => this.handleDraftChange(), loopTime);
    this.draftTip = this.querySelector('.editor-draft-tip');
    this.draftTipContent = this.draftTip.querySelector('.tip-content');
    this.renderTip();
  },

  handleMDEditorReady(event) {
    const { codemirror } = event.detail.editor;
    codemirror.on('change', debounce(() => this.handleDraftChange(), waitingTime));
    this.draftData = this.getDraftData();
  },

  handleCancel(event) {
    if (!this.draftUpdatedAt || !this.draftDestroyUrl) return;
    event.preventDefault();

    const dialog = showConfirmation({
      content: '确定要丢弃未发布的草稿吗？',
      buttons: [
        {
          text: '丢弃草稿',
          action: 'yes',
          cls: 'button-danger',
        },
        {
          text: '保留草稿',
          action: 'no',
          cls: 'button-text',
        },
      ],
    });

    dialog.addEventListener('tr-dialog:closed', (e) => {
      if (this.cancel) this.cancel();
      if (e.detail === 'yes') {
        this.clearDraftData();
        this.destryDraft();
      }
    });
  },

  handleDraftChange() {
    if (moment().diff(this.draftUpdatedAt) < waitingTime) return;
    const data = this.getDraftData();
    if (isEqual(data, this.draftData)) return;
    this.updateDraft(data);
  },

  getDraftData() {
    const data = {};

    [...this.querySelectorAll('tr-editor')].forEach((element) => {
      const { editor } = element;
      if (!editor) return;
      Object.assign(data, { [editor.sourceElement.name]: editor.getData() });
    });

    [...this.querySelectorAll('tr-markdown-editor')].forEach((element) => {
      const { editor } = element;
      if (!editor) return;
      Object.assign(data, { [editor.element.name]: editor.value() });
    });

    [...this.querySelectorAll('[data-draft-field="true"]')].forEach((element) => {
      if (element.nodeName.toLowerCase() === 'tr-text-field') {
        Object.assign(data, { [element.nativeField.name]: element.nativeField.value });
      } else {
        Object.assign(data, { [element.name]: element.value });
      }
    });

    return data;
  },

  checkDraftChange(data) {
    if (isEqual(data, this.draftData)) return false;
    this.draftData = data;
    return true;
  },

  updateDraft(data) {
    if (this.draftRequest) this.draftRequest.abort();
    if (this.draftDisable) return;
    this.draftData = data;
    this.renderTip('waiting');
    this.draftRequest = $.ajax({
      url: this.draftUrl,
      type: 'post',
      dataType: 'json',
      data: { form_data: JSON.stringify(data) },
      success: () => {
        this.draftUpdatedAt = moment().format();
        this.renderTip('saved');
        setTimeout(() => this.renderTip(), 3000);
      },
    });
  },

  destryDraft() {
    $.ajax({
      url: this.draftDestroyUrl,
      type: 'delete',
      dataType: 'script',
    });
  },

  clearDraftData() {
    if (!this.draftClearable) return;
    if (this.setContent) this.setContent('');
    this.draftData = this.getDraftData();
    this.draftUpdatedAt = '';
    this.renderTip('clear');
  },

  renderTip(type = '') {
    if (!this.draftTip) return;
    let html = '';

    if (type === 'waiting') {
      html = '正在保存...';
    } else if (type === 'saved') {
      html = '草稿已自动保存';
    } else if (type === 'clear') {
      html = '';
    } else if (this.draftUpdatedAt) {
      html = `最后修改于 <tr-readable-datetime type="time" date="${this.draftUpdatedAt}"></tr-readable-datetime>`;
    }

    this.draftTip.dataset.type = type;
    this.draftTipContent.innerHTML = html;
  },

  disconnected() {
    clearInterval(this.draftTimer);
  },
};
